import React from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useModules } from '../../context/ModuleContext';
import { Badge } from '../../design-system/Badge';
import {
  Home,
  CheckSquare,
  Inbox,
  Compass,
  Sliders,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Layers,
  Users
} from 'lucide-react';

export const Sidebar = ({ currentView, setCurrentView, currentProjectView, setCurrentProjectView }) => {
  const {
    activeWorkspace,
    projects,
    activeProject,
    setActiveProjectId,
    allTasks,
    isSidebarCollapsed,
    toggleSidebar,
    setIsCreateProjectModalOpen
  } = useWorkspace();
  const { modules, enabledModules } = useModules();

  const myTasksCount = allTasks.filter((t) => t.status !== 'done').length;

  // Filter modules that are active in this workspace
  const activeWorkModules = modules.filter((m) => enabledModules.includes(m.id));

  return (
    <aside
      style={{
        width: isSidebarCollapsed ? '68px' : '260px',
        backgroundColor: 'var(--surface-alt)',
        borderRight: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        position: 'sticky',
        top: '64px',
        overflowY: 'auto',
        overflowX: 'hidden',
        userSelect: 'none',
        flexShrink: 0,
        transition: 'width 0.22s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
      className="sidebar-container"
    >
      {/* Collapse Toggle Row */}
      <div
        style={{
          padding: '10px 12px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isSidebarCollapsed ? 'center' : 'space-between'
        }}
      >
        {!isSidebarCollapsed && (
          <span
            style={{
              fontSize: '11px',
              fontWeight: '700',
              color: 'var(--text-muted)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            Workspace Nav
          </span>
        )}
        <button
          onClick={toggleSidebar}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s'
          }}
          title={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isSidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
        </button>
      </div>

      {/* Top Section: Global Workspace Nav */}
      <div style={{ padding: '6px 10px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <button
          onClick={() => setCurrentView('workspace_home')}
          title="Home"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
            gap: '10px',
            padding: isSidebarCollapsed ? '10px' : '8px 12px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'workspace_home' ? 'var(--surface-card)' : 'transparent',
            boxShadow: currentView === 'workspace_home' ? 'var(--shadow-raised-sm)' : 'none',
            color: currentView === 'workspace_home' ? 'var(--accent-teal)' : 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: currentView === 'workspace_home' ? '700' : '450',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left',
            transition: 'all 0.15s'
          }}
        >
          <Home size={17} />
          {!isSidebarCollapsed && <span>Home</span>}
        </button>

        <button
          onClick={() => setCurrentView('my_work')}
          title={`My Work (${myTasksCount})`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
            gap: '10px',
            padding: isSidebarCollapsed ? '10px' : '8px 12px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'my_work' ? 'var(--surface-card)' : 'transparent',
            boxShadow: currentView === 'my_work' ? 'var(--shadow-raised-sm)' : 'none',
            color: currentView === 'my_work' ? 'var(--accent-teal)' : 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: currentView === 'my_work' ? '700' : '450',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left',
            position: 'relative',
            transition: 'all 0.15s'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <CheckSquare size={17} />
            {!isSidebarCollapsed && <span>My Work</span>}
          </span>
          {!isSidebarCollapsed ? (
            <Badge size="xs" variant={currentView === 'my_work' ? 'teal' : 'recessed'}>
              {myTasksCount}
            </Badge>
          ) : (
            <span
              style={{
                position: 'absolute',
                top: '6px',
                right: '8px',
                width: '6px',
                height: '6px',
                borderRadius: '999px',
                backgroundColor: 'var(--accent-teal)'
              }}
            />
          )}
        </button>

        <button
          onClick={() => setCurrentView('inbox')}
          title="Inbox"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
            gap: '10px',
            padding: isSidebarCollapsed ? '10px' : '8px 12px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'inbox' ? 'var(--surface-card)' : 'transparent',
            boxShadow: currentView === 'inbox' ? 'var(--shadow-raised-sm)' : 'none',
            color: currentView === 'inbox' ? 'var(--accent-teal)' : 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: currentView === 'inbox' ? '700' : '450',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left',
            position: 'relative',
            transition: 'all 0.15s'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <Inbox size={17} />
            {!isSidebarCollapsed && <span>Inbox</span>}
          </span>
          <span
            style={{
              position: isSidebarCollapsed ? 'absolute' : 'static',
              top: isSidebarCollapsed ? '6px' : 'auto',
              right: isSidebarCollapsed ? '8px' : 'auto',
              width: '7px',
              height: '7px',
              borderRadius: '999px',
              backgroundColor: 'var(--accent-teal)'
            }}
          />
        </button>

        <button
          onClick={() => setCurrentView('projects_directory')}
          title="Projects Directory"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
            gap: '10px',
            padding: isSidebarCollapsed ? '10px' : '8px 12px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'projects_directory' ? 'var(--surface-card)' : 'transparent',
            boxShadow: currentView === 'projects_directory' ? 'var(--shadow-raised-sm)' : 'none',
            color: currentView === 'projects_directory' ? 'var(--accent-teal)' : 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: currentView === 'projects_directory' ? '700' : '450',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left',
            transition: 'all 0.15s'
          }}
        >
          <Layers size={17} />
          {!isSidebarCollapsed && <span>Projects Directory</span>}
        </button>

        <button
          onClick={() => setCurrentView('members')}
          title="Members & Teams"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
            gap: '10px',
            padding: isSidebarCollapsed ? '10px' : '8px 12px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'members' ? 'var(--surface-card)' : 'transparent',
            boxShadow: currentView === 'members' ? 'var(--shadow-raised-sm)' : 'none',
            color: currentView === 'members' ? 'var(--accent-teal)' : 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: currentView === 'members' ? '700' : '450',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left',
            transition: 'all 0.15s'
          }}
        >
          <Users size={17} />
          {!isSidebarCollapsed && <span>Members & Access</span>}
        </button>
      </div>

      <div style={{ height: '1px', backgroundColor: 'var(--border-divider)', margin: '4px 12px' }} />

      {/* Projects Section */}
      <div style={{ padding: '8px 10px 6px' }}>
        {!isSidebarCollapsed && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 8px 8px',
              fontSize: '11px',
              fontWeight: '700',
              color: 'var(--text-muted)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            <span>Projects</span>
            <button
              onClick={() => setIsCreateProjectModalOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-teal)',
                cursor: 'pointer',
                padding: '2px',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Create New Project"
            >
              <Plus size={14} />
            </button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {projects.filter(p => (p.status || 'Active') === 'Active' && p.workspaceId === activeWorkspace?.id).map((prj) => {
            const isSelected = activeProject?.id === prj.id && currentView === 'project_view';
            return (
              <button
                key={prj.id}
                onClick={() => {
                  setActiveProjectId(prj.id);
                  setCurrentView('project_view');
                }}
                title={`${prj.name} (${prj.key})`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  gap: '8px',
                  padding: isSidebarCollapsed ? '8px 4px' : '7px 10px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: isSelected ? 'var(--surface-card)' : 'transparent',
                  boxShadow: isSelected ? 'var(--shadow-raised-sm)' : 'none',
                  color: isSelected ? 'var(--accent-teal)' : 'var(--text-primary)',
                  fontSize: '13px',
                  fontWeight: isSelected ? '700' : '450',
                  cursor: 'pointer',
                  border: isSelected ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  textAlign: 'left',
                  transition: 'all 0.15s'
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: '800',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    backgroundColor: isSelected ? 'rgba(35, 190, 179, 0.2)' : 'var(--surface-recessed)',
                    color: isSelected ? 'var(--accent-teal)' : 'var(--text-muted)'
                  }}
                >
                  {prj.key}
                </span>
                {!isSidebarCollapsed && (
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {prj.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ height: '1px', backgroundColor: 'var(--border-divider)', margin: '4px 12px' }} />

      {/* Active Project Modules Section */}
      <div style={{ padding: '8px 10px 6px', flex: 1 }}>
        {!isSidebarCollapsed && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 8px 8px',
              fontSize: '11px',
              fontWeight: '700',
              color: 'var(--text-muted)',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            <span>Active Modules</span>
            <span style={{ fontSize: '11px', color: 'var(--accent-teal)', fontWeight: '600' }}>
              {activeWorkModules.length} enabled
            </span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {activeWorkModules.map((mod) => {
            const isActive = currentView === 'project_view' && currentProjectView === mod.id;
            return (
              <button
                key={mod.id}
                onClick={() => {
                  setCurrentView('project_view');
                  setCurrentProjectView(mod.id);
                }}
                title={mod.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                  gap: '10px',
                  padding: isSidebarCollapsed ? '10px' : '7px 10px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: isActive ? 'var(--surface-recessed)' : 'transparent',
                  boxShadow: isActive ? 'var(--shadow-recessed-sm)' : 'none',
                  color: isActive ? 'var(--accent-teal)' : 'var(--text-secondary)',
                  fontSize: '13px',
                  fontWeight: isActive ? '700' : '450',
                  cursor: 'pointer',
                  border: isActive ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  textAlign: 'left',
                  transition: 'all 0.15s'
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: '18px',
                    color: isActive ? 'var(--accent-teal)' : 'var(--text-muted)'
                  }}
                >
                  {mod.icon}
                </span>
                {!isSidebarCollapsed && (
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {mod.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer Navigation: Module Library & Settings */}
      <div
        style={{
          padding: isSidebarCollapsed ? '12px 6px' : '12px',
          borderTop: '1px solid var(--border-divider)',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          backgroundColor: 'var(--surface-card)'
        }}
      >
        <button
          onClick={() => setCurrentView('module_library')}
          title="Module Library"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
            padding: isSidebarCollapsed ? '10px' : '8px 10px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'module_library' ? 'var(--surface-recessed)' : 'transparent',
            color: currentView === 'module_library' ? 'var(--accent-teal)' : 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <Compass size={16} color="var(--accent-teal)" />
            {!isSidebarCollapsed && <span>Module Library</span>}
          </span>
          {!isSidebarCollapsed && (
            <Badge size="xs" variant="teal">
              +Add
            </Badge>
          )}
        </button>

        <button
          onClick={() => setCurrentView('settings')}
          title="Workspace Settings"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
            gap: '8px',
            padding: isSidebarCollapsed ? '10px' : '8px 10px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: currentView === 'settings' ? 'var(--surface-recessed)' : 'transparent',
            color: currentView === 'settings' ? 'var(--accent-teal)' : 'var(--text-secondary)',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            border: 'none',
            textAlign: 'left'
          }}
        >
          <Sliders size={16} />
          {!isSidebarCollapsed && <span>Settings</span>}
        </button>
      </div>
    </aside>
  );
};
